import React from "react";
import styled from "styled-components";
import { Col, Row } from "react-bootstrap";
import {
  COLOR_GRAY_50,
  COLOR_GRAY_MUTED,
} from "../common/colors";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { AppFlowActionButton, BackNavRow, PageContainer, AppAnchor } from "../common/CommonStyles";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import {
  VerifyAgeContext,
  VerifyStyle,
} from "../common/context/VerifyAgeContext";
import { AccountContext } from "../common/context/AccountContext";

const DataBox = styled(Col)`
  border-radius: 12px;
  background-color: ${COLOR_GRAY_50};
  height: 100%;
  margin: auto;
  padding: 16px 12px;
  text-align: center;
  max-width: 300px;
`;

const DataTitle = styled.div`
  font-weight: 500;
  font-size: 24px;
`;

const DataDescription = styled.div`
  font-size: 14px;
  line-height: 18px;
  color: ${COLOR_GRAY_MUTED};
  margin-top: 12px;
`;

const ContinueButton = styled(AppFlowActionButton)`
  font-size: 24px;
`;

export const DataHandling: React.FC<{ showsTermsOfUseConsent?: boolean }> = ({
  showsTermsOfUseConsent = true,
}) => {
  const [hasAccepted, setHasAccepted] = React.useState(false);
  const { agreeToTermsOfUse } = React.useContext(AccountContext);
  const { verifyAge } = React.useContext(VerifyAgeContext);
  const { t } = useTranslation();

  return (
    <PageContainer>
      {showsTermsOfUseConsent && (
        <BackNavRow label={t("generic_nav_back")}>
          <h2>{t("data_handling_title")}</h2>
        </BackNavRow>
      )}
      <div className="text-center my-5">
        {!showsTermsOfUseConsent && <h2>{t("data_handling_title")}</h2>}

        {t("data_handling_explanation")}
      </div>
      <Row className="my-3" style={{ gap: "20px" }}>
        <DataBox>
          <DataTitle>{t("data_handling_collection_title")}</DataTitle>
          <DataDescription>
            {t("data_handling_collection_body")}
          </DataDescription>
        </DataBox>
        <DataBox>
          <DataTitle>{t("data_handling_sharing_title")}</DataTitle>
          <DataDescription>{t("data_handling_sharing_body")}</DataDescription>
        </DataBox>
        <DataBox>
          <DataTitle>{t("data_handling_usage_title")}</DataTitle>
          <DataDescription>{t("data_handling_usage_body")}</DataDescription>
        </DataBox>
      </Row>
      <div>
        <Trans t={t} i18nKey="data_handling_explanation2">
          In the free trial version of Super Teacher, as well as for school
          accounts, we discard audio promptly after transcription, discard
          transcriptions promptly after lessons, and don't use or share the data
          for any other purpose. For more details, see our{" "}
          <AppAnchor href="/privacy-policy/" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </AppAnchor>
          .
        </Trans>
      </div>
      {showsTermsOfUseConsent && (
        <>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasAccepted}
                  onChange={(e) => {
                    if (e.target.checked) {
                      verifyAge((success: boolean) => {
                        if (success) {
                          setHasAccepted(true);
                        }
                      }, VerifyStyle.DATA_HANDLING);
                    } else {
                      setHasAccepted(false);
                    }
                  }}
                />
              }
              label={
                <span>
                  <Trans t={t} i18nKey="data_handling_explanation_checkbox">
                    I agree to the Super Teacher{" "}
                    <AppAnchor
                      href="/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </AppAnchor>
                    , as well as its{" "}
                    <AppAnchor
                      href="/terms-of-use/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Use
                    </AppAnchor>
                  </Trans>
                </span>
              }
              style={{ margin: "20px auto 0 auto" }}
            />
          </FormGroup>

          <ContinueButton
            disabled={!hasAccepted}
            isEnabled={hasAccepted}
            onClick={() => {
              if (!hasAccepted) {
                return;
              }
              agreeToTermsOfUse();
            }}
          >
            {t("data_handling_continue")}
          </ContinueButton>
        </>
      )}
    </PageContainer>
  );
};
